import React, {useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {
  accountToFormValues,
  locationToFormValues,
} from '../../AllAccounts/AllAccountsFormMapping'
import {LocationForm} from '../../../AccountInfo/components/LocationForm'
import {AddressSuggestionBanner} from '../../../AccountInfo/components/AddressSuggestionBanner'
import {
  LOCATION_FIELDS,
  ORDER_FLOW_VISIBLE_FIELD_KEYS,
} from '../../../AccountInfo/config/locationFields'
import {Spinner} from '../../../../../components/loader-spinner'
import {selectEffectiveRole} from '../../../../../app-store/selector'
import {
  FormCard,
  TypeBadge,
  FormHeader,
  FormBody,
} from './AccountEditModal.style'
import {buildFieldConfigMap} from '../../../AccountInfo/config/locationFieldRules'
import {validateField} from '../../../AccountInfo/utils/formHelpers'
import {useUserFeatures} from '../../../../../context/user-features-context'

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 1a4 4 0 0 1 4 4c0 3-4 8-4 8S3 8 3 5a4 4 0 0 1 4-4z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="7" cy="5" r="1.2" fill="currentColor" />
  </svg>
)

const hasPartnerOpsRole = roles => {
  const roleList = Array.isArray(roles) ? roles : roles ? [roles] : []
  return roleList.some(
    role => String(role).toLowerCase().replace(/\s/g, '') === 'partnerops',
  )
}

const applyPartnerOpsDefaults = (formValues, roles) => {
  const hasXpCode = String(formValues?.xpCode || '').trim()
  if (!hasPartnerOpsRole(roles) || hasXpCode) return formValues
  // PartnerOps only has the Keap Partner / Legacy Partner options (no plain
  // 'partner'), so default to 'keap-partner' to keep a valid option selected.
  return {...formValues, salesRepOrPartner: 'keap-partner'}
}

const buildInitialValues = ({account, parentAccount, roles, type}) => {
  if (type === 'parent') {
    return applyPartnerOpsDefaults(accountToFormValues(account), roles)
  }

  const email = parentAccount?.email || parentAccount?.accountEmail
  const base = locationToFormValues(account)

  if (type === 'add-location') {
    if (email) {
      base.email = email
    }
    base.billTo = 'parent'
  }

  return applyPartnerOpsDefaults(base, roles)
}

export const AccountEditModal = ({
  roles,
  account,
  type = 'parent',
  loading = false,
  saving = false,
  isValidatingAddress = false,
  onValuesChange,
  parentAccount,
  addressErrors = {},
  frontendOverrideSst,
  hideLocationDetails = false,
  isOrderFlow = false,
  isPre5 = false,
  addressSuggestion = null,
  onApplySuggestion,
  valuesOverride,
  hideTypeBadge = false,
  additionalHiddenFields = [],
  suggestionApplyLabel,
}) => {
  const selectedRoles = useSelector(selectEffectiveRole)
  const effectiveRoles = roles ?? selectedRoles
  const {Enable_Internal_Account_Checkbox} = useUserFeatures()
  const [values, setValues] = useState(
    () =>
      valuesOverride ||
      buildInitialValues({account, parentAccount, roles: effectiveRoles, type}),
  )

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const country =
    type === 'add-location'
      ? parentAccount?.address?.country || parentAccount?.country
      : account?.address?.country
  const isChild = type === 'child' || type === 'add-location'
  const accountForRules = {
    ...(account || {}),
    type: type === 'parent' ? 'parent' : 'child',
  }
  // FULSAS-11828: only meaningful when editing an EXISTING child (never 'add-location' — a
  // brand new location has nothing stored yet). Backed by RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME
  // via account-service.js getAccountById, exposed here as account.locationName.
  const hasStoredLocationName =
    type === 'child' && Boolean(String(account?.locationName || '').trim())

  // Re-initialize form values whenever a fresh account arrives (e.g. after GET fetch).
  useEffect(() => {
    if (!account && type !== 'add-location') return
    const initial =
      valuesOverride ||
      buildInitialValues({
        account,
        parentAccount,
        roles: effectiveRoles,
        type,
      })
    setValues(initial)
    onValuesChange?.(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, effectiveRoles])

  const validate = (currentValues, field, value) => {
    const fieldConfigMap = buildFieldConfigMap({
      roles: effectiveRoles,
      account: {...accountForRules, values: currentValues},
      values: currentValues,
      selectedCountry: country,
      selectedTransactionType:
        type === 'parent' ? 'single-location' : 'multiple-locations',
      states: [],
      isAddLocation: type === 'add-location',
      isChildLocation: isChild,
      hasStoredLocationName,
      frontendOverrideSst,
      enableInternalAccountCheckbox: Enable_Internal_Account_Checkbox,
    })
    return validateField(
      {values: currentValues, country},
      value,
      field,
      fieldConfigMap,
    )
  }

  const handleChange = (field, value) => {
    const updated = {...values, [field]: value}
    setValues(updated)
    setTouched(prev => ({...prev, [field]: true}))
    onValuesChange?.(updated)
    const fieldErrors = validate(updated, field, value)
    setErrors(prev => {
      const rest = Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== field),
      )
      return {...rest, ...fieldErrors}
    })
  }

  const handleBlur = field => {
    setTouched(prev => ({...prev, [field]: true}))
    const fieldErrors = validate(values, field, values[field])
    setErrors(prev => {
      const rest = Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== field),
      )
      return {...rest, ...fieldErrors}
    })
  }

  // Fills in the address/city/state/postCode fields from the Google-recommended
  // address (see AddressSuggestionBanner) in one shot, same as handleChange but
  // for several fields at once. Passes the full merged values (not just the
  // recommended subset) up to the parent so it can save immediately with
  // them — see onApplySuggestion callers, which save directly instead of
  // re-running address validation on the recommendation Google already gave.
  const handleApplySuggestion = recommendedValues => {
    const updated = {...values, ...recommendedValues}
    setValues(updated)
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(Object.keys(recommendedValues).map(f => [f, true])),
    }))
    onValuesChange?.(updated)
    setErrors(prev => {
      const rest = Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => !Object.keys(recommendedValues).includes(key),
        ),
      )
      return rest
    })
    onApplySuggestion?.(updated)
  }

  const badgeLabel = isChild ? 'Child Location' : 'Account'
  const mergedErrors = {...errors, ...addressErrors}

  // Base hidden fields (unchanged behavior).
  const baseHiddenFields =
    type === 'parent' ? ['country', 'billTo'] : ['country']

  // ORDER_FLOW_VISIBLE_FIELD_KEYS is the set of order-time attribution fields
  // (on-behalf-of Sales/Partner + override SST). Two flows adjust which fields show:
  //   • "Create Purchase Link" (hideLocationDetails): hide the entire identity/address
  //     block, keeping ONLY those order-attribution fields.
  //   • Editing a 5.0+ parent account (type 'parent' + !isPre5, not an order flow):
  //     the reverse — hide the order-attribution fields, since they are not relevant
  //     when just editing account details. Pre-5.0 (legacy) parents keep them.
  let extraHiddenFields = []
  if (hideLocationDetails) {
    extraHiddenFields = LOCATION_FIELDS.map(field => field.key).filter(
      key => !ORDER_FLOW_VISIBLE_FIELD_KEYS.includes(key),
    )
  } else if (type === 'parent' && !isPre5) {
    extraHiddenFields = ORDER_FLOW_VISIBLE_FIELD_KEYS
  }

  const hiddenFields = [
    ...new Set([
      ...baseHiddenFields,
      ...extraHiddenFields,
      ...additionalHiddenFields,
    ]),
  ]
  const isParentBilledChildOrder =
    isOrderFlow &&
    type === 'child' &&
    values.billTo === 'parent' &&
    account?.abnInheritedFromParent
  const disabledFields = [
    ...(type !== 'add-location' ? ['email', 'billTo'] : []),
    ...(isParentBilledChildOrder ? ['abn'] : []),
  ]

  return (
    <FormCard style={{position: 'relative'}}>
      {(saving || isValidatingAddress) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 'inherit',
          }}
        >
          <Spinner />
        </div>
      )}
      {!hideLocationDetails && !hideTypeBadge && (
        <FormHeader>
          <TypeBadge>
            <PinIcon />
            {badgeLabel}
          </TypeBadge>
        </FormHeader>
      )}

      <FormBody>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {!hideLocationDetails && (
              <AddressSuggestionBanner
                suggestion={addressSuggestion}
                onApply={handleApplySuggestion}
                applyLabel={suggestionApplyLabel}
              />
            )}
            <LocationForm
              roles={effectiveRoles}
              account={accountForRules}
              values={values}
              errors={mergedErrors}
              touched={touched}
              onChange={handleChange}
              onBlur={handleBlur}
              country={country}
              isAddLocation={type === 'add-location'}
              isChildLocation={isChild}
              hasStoredLocationName={hasStoredLocationName}
              hiddenFields={hiddenFields}
              disabledFields={disabledFields}
            />
          </>
        )}
      </FormBody>
    </FormCard>
  )
}
